// SPDX-FileCopyrightText: 2024 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { nativeVerticalScrollStyles } from 'components/common/scroll/nativeScrollStyles';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Role } from 'model/Role';
import { Checkbox, Footer, FooterButton, InputFieldText } from '@tls/treact-ui';
import { IconLoading } from '@tls/treact-ui/icons';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: calc(100% - 1px + 1px);
`;

const Form = styled.form`
  flex-grow: 1;
  ${nativeVerticalScrollStyles};
`;

const Grid = styled.div`
  display: grid;
  padding: 1rem;
  grid-template-columns: repeat(1, 1fr);
  grid-gap: 1rem;
`;
const GridItem = styled.div``;

enum EditAction {
  Save,
  Delete,
}
export default function RoleDetails({
  role,
  roles,
  addOrEditRole,
  deleteRole,
  onClose,
  isLoading = false,
  isBuiltInRole,
  permissions = [],
}: Readonly<Props>) {
  const { t } = useTranslation();

  const [editRole, setEditRole] = useState(role);
  const [action, setAction] = useState<EditAction>();

  const isNameAlreadyExist = useMemo(
    () => (roles?.filter(otherRole => otherRole.id !== role.id && otherRole.name === editRole.name)?.length ?? 0) > 0,
    [editRole, role, roles]
  );

  const onDelete = useCallback(async () => {
    setAction(EditAction.Delete);
    if (deleteRole) await deleteRole(role);
    setAction(undefined);
    if (onClose) onClose();
  }, [deleteRole, onClose, role]);

  const onAccept = useCallback(async () => {
    setAction(EditAction.Save);
    await addOrEditRole(editRole);
    setAction(undefined);
    if (onClose) onClose();
  }, [editRole, onClose, addOrEditRole]);

  useEffect(() => {
    setEditRole(role);
  }, [role]);

  const hasRoleChanges = useMemo(() => JSON.stringify(editRole) !== JSON.stringify(role), [editRole, role]);

  const editing = action === EditAction.Save;

  const rightActions: React.JSX.Element[] = [];
  if (onClose) {
    rightActions.push(
      <FooterButton
        key='abort'
        variant={'secondary'}
        label={t('cancel')}
        onClick={onClose}
        disabled={isLoading}
        data-testid='settings-role-cancel-button'
      />
    );
  }
  rightActions.push(
    <FooterButton
      key='save'
      variant={'primary'}
      label={t('save')}
      onClick={onAccept}
      icon={editing ? <IconLoading /> : undefined}
      disabled={isLoading || !hasRoleChanges || editing || isBuiltInRole}
      data-testid='settings-role-save-button'
    />
  );

  const togglePermission = useCallback(
    (newPermission: string) => {
      if (editRole?.permissions?.includes(newPermission)) {
        setEditRole({
          ...editRole,
          permissions: editRole.permissions?.filter(perm => perm !== newPermission) ?? [],
        });
      } else if (editRole) {
        setEditRole({
          ...editRole,
          permissions: [...(editRole.permissions ?? []), newPermission],
        });
      }
    },
    [editRole, setEditRole]
  );

  if (editRole.id !== role.id) return <></>;

  const deleting = action === EditAction.Delete;

  return (
    <Root>
      <Form autoComplete='off'>
        <Grid>
          <GridItem>
            <InputFieldText
              label={t('role.roleName')}
              value={editRole.name}
              type={isNameAlreadyExist ? 'error' : 'none'}
              helperText={isNameAlreadyExist ? t('role.validation.roleNameError') : undefined}
              data-testid='settings-role-name-input'
              onValueChange={newValue =>
                setEditRole({
                  ...editRole,
                  name: newValue,
                })
              }
              disabled={isBuiltInRole}
            />
          </GridItem>
          <GridItem>
            <p className='font-size_m font-weight_bold title'>{t('role.permission.name')}</p>
            {permissions?.map(p => (
              <LeftAlignedCheckbox
                key={p}
                disabled={role.isBuiltIn}
                label={p}
                value={
                  role.isBuiltIn || editRole.permissions?.includes(p)
                    ? Checkbox.Selection.selected
                    : Checkbox.Selection.unselected
                }
                onValueChange={() => togglePermission(p)}
                data-testid={`settings-role-permission-${p}-checkbox`}
              />
            ))}
          </GridItem>
        </Grid>
      </Form>
      <Footer
        left={
          deleteRole
            ? [
                <FooterButton
                  key='delete'
                  variant={'secondary'}
                  label={t('delete')}
                  onClick={onDelete}
                  icon={deleting ? <IconLoading /> : undefined}
                  disabled={isLoading || deleting || isBuiltInRole}
                  data-testid='settings-role-delete-button'
                />,
              ]
            : []
        }
        right={rightActions}
      />
    </Root>
  );
}

interface Props {
  role: Role;
  roles?: Role[];
  onClose?: () => void;
  addOrEditRole: (role: Role) => Promise<unknown>;
  deleteRole?: (role: Role) => Promise<unknown>;
  isBuiltInRole?: boolean;
  permissions?: string[];
  isLoading?: boolean;
}

const LeftAlignedCheckbox = styled(Checkbox)`
  align-items: start;
`;
